import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete } from '@mui/material';
import { Formik } from "formik";
import React, { useRef } from "react";
import { Loadable, TextMaskCustom } from "../../../common";
import { STATES } from "../../../store/constants/clients";
import SearchIcon from "@mui/icons-material/Search";

const useStyles = makeStyles(theme => ({
    textField: {
        width: "90%"
    },
    formControlAuto: {
        marginTop: theme.spacing(5),
        width: "90%"
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    formControl: {
        marginTop: theme.spacing(2),
        width: "90%"
    },
    autoComplete: {
        width: '90%',
    },
    switch: {
        marginLeft: '0px',
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(1),
    },
}));

export default function ProvidersEditDialog({
    intl,
    open,
    handleClose,
    data,
    mode,
    initialValues,
    providerCreatePending,
    providerUpdatePending,
    searchPending,
    getInitialFormattedState,
    npiRoles,
    actions: {createProvider ,updateProvider, searchFromNPI }
}) {
    const classes = useStyles();
    const formikRef = useRef<any>();

    function handleCallback(reload) {
        handleClose(reload);
    }

    const handleSearchFromNPIRegistry = (npi) => {
        searchFromNPI(npi, searchCallback);
    }

    const searchCallback = (data) => {
        if(formikRef.current && data) {
            if(data?.providerName?.trim()) {
                formikRef.current.setFieldValue('providerName', data?.providerName?.trim());
            }
            if(data?.providerType?.trim()) {
                formikRef.current.setFieldValue('providerType', data?.providerType?.trim());
            }
            if(data?.specialty?.trim()) {
                formikRef.current.setFieldValue('specialty', data?.specialty?.trim());
            }
            if(data?.state?.trim()) {
                formikRef.current.setFieldValue('state', data?.state?.trim() ? getInitialFormattedState(data?.state?.trim(), STATES) : null);
            }
            if(data?.status?.trim()) {
                formikRef.current.setFieldValue('status', data?.status?.trim());
            }
            if(data?.taxonomyCode?.trim()) {
                formikRef.current.setFieldValue('taxonomyCode', data?.taxonomyCode?.trim());
            }
        }
    }

    return (
        (<Dialog
                maxWidth="md"
                fullWidth
                open={open}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
            >
            <Loadable loading={searchPending}>
            <DialogTitle id="form-dialog-title">
                <Grid container>
                    <Grid item xs={6}>
                        <Typography variant="h6">
                            {mode === 'edit' ? 'Edit Provider' : 'Add Provider'}
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <IconButton
                            aria-label="close"
                            className={classes.closeButton}
                            onClick={handleClose}
                            size="large">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                <Loadable loading={providerCreatePending || providerUpdatePending}>
                    <Formik
                        innerRef={formikRef}
                        enableReinitialize={true}
                        initialValues={initialValues}
                        validate={values => {
                            const errors:any = {};
                            const npiRegex = /^\d{10}$/;
                            const taxonomyRegex = /^[A-Za-z0-9]{10}$/;
                            const providerTypeRegex = /^[A-Za-z,\- ]+$/;

                            if (!values.npi) {
                                errors.npi = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                            }

                            if (values?.npi && !npiRegex.test(values?.npi )) {
                                errors.npi = "NPI must be a 10 Digit Number";
                            }

                            if (!values.providerName) {
                                errors.providerName = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                            }

                            if (!values.providerType) {
                                errors.providerType = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                            }

                            if (values?.providerType && !providerTypeRegex.test(values.providerType)) {
                                errors.providerType = "Provider type can contain only alphabets, spaces, hyphens (-), and commas (,)";
                            }

                            if (values?.taxonomyCode && !taxonomyRegex.test(values?.taxonomyCode)) {
                                errors.taxonomyCode = "Taxonomy code must be 10 alphanumeric characters";
                            }

                            return errors;
                        }}
                        onSubmit={(values, { setStatus, setSubmitting }) => {
                            let payload = {
                                id: undefined,
                                npi: values.npi?.trim(),
                                providerName: values.providerName?.trim(),
                                providerType: values.providerType?.trim(),
                                degree: values.degree?.trim(),
                                taxonomyCode: values.taxonomyCode?.trim(),
                                specialty: values.specialty?.trim(),
                                tin: values.tin?.trim() === "-" ? "" : values.tin?.trim(),
                                isActive: values.isActive,
                                primaryFacility: values.primaryFacility?.trim(),
                                state: typeof values.state === 'object' && values.state !== null ? values.state.abbreviation : values.state?.trim(),
                                status: values.status?.trim(),
                                npiroleId: values.npiroleId
                            }
                            if (mode === 'create') {
                                createProvider(payload, handleCallback);
                            }
                            else {
                                payload = {
                                    ...payload,
                                    id: values.id,
                                    
                                }
                                updateProvider(payload, handleCallback);
                            }
                        }}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            isSubmitting
                        }) => {

                            return (
                                <form
                                    onSubmit={handleSubmit}
                                    noValidate
                                    autoComplete="off"
                                >
                                <Grid container>
                                    <Grid item xs={6}>
                                        <TextField
                                            required
                                            label="NPI"
                                            name="npi"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.npi}
                                            helperText={touched.npi && errors.npi}
                                            error={Boolean(touched.npi && errors.npi)}
                                            inputProps={{ maxLength: 10 }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        sx={{ padding: 0 }}
                                                        onClick={() =>
                                                            values?.npi?.trim().length === 10 && handleSearchFromNPIRegistry(values?.npi?.trim())
                                                        }
                                                    >
                                                        <SearchIcon sx={{ fontSize: "14px" }} />
                                                    </IconButton>
                                                ),
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    if (values?.npi?.trim().length === 10) {
                                                        handleSearchFromNPIRegistry(values?.npi?.trim());
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            required
                                            label="Provider Name"
                                            name="providerName"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.providerName}
                                            helperText={touched.providerName && errors.providerName}
                                            error={Boolean(touched.providerName && errors.providerName)}
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={6}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor="npiRoleId">Role</InputLabel>
                                            <Select
                                                value={values.npiroleId}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name="npiroleId"
                                                error={Boolean(touched.npiroleId && errors.npiroleId)}
                                            >
                                                {npiRoles.map(role => (
                                                    <MenuItem
                                                        key={role.id}
                                                        value={role.id}
                                                    >
                                                        {role.name}
                                                    </MenuItem>
                                                ))}

                                            </Select>
                                            <FormHelperText error={Boolean(errors.npiroleId)}>{errors.npiroleId}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            required
                                            label="Provider Type"
                                            name="providerType"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.providerType}
                                            helperText={touched.providerType && errors.providerType}
                                            error={Boolean(touched.providerType && errors.providerType)}
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Degree"
                                            name="degree"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.degree}
                                            helperText={touched.degree && errors.degree}
                                            error={Boolean(touched.degree && errors.degree)}
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Taxonomy Code"
                                            name="taxonomyCode"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.taxonomyCode}
                                            helperText={touched.taxonomyCode && errors.taxonomyCode}
                                            error={Boolean(touched.taxonomyCode && errors.taxonomyCode)}
                                            inputProps={{ maxLength: 10 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Specialty"
                                            name="specialty"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.specialty}
                                            helperText={touched.specialty && errors.specialty}
                                            error={Boolean(touched.specialty && errors.specialty)}
                                            inputProps={{ maxLength: 250 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="TIN"
                                            name="tin"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.tin}
                                            helperText={touched.tin && errors.tin}
                                            error={Boolean(touched.tin && errors.tin)}
                                            inputProps={{
                                                mask: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
                                            }}
                                            InputProps={{
                                                inputComponent: TextMaskCustom,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={6}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor="status">Status</InputLabel>
                                            <Select
                                                value={values.status}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name="status"
                                                error={Boolean(touched.status && errors.status)}
                                            >
                                                <MenuItem value="Active">Active</MenuItem>
                                                <MenuItem value="Inactive">Inactive</MenuItem>

                                            </Select>
                                            <FormHelperText error={Boolean(errors.status)}>{errors.status}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Autocomplete
                                            className={classes.autoComplete}
                                            value={values.state}
                                            disableClearable={false}
                                            onChange={(e, value) => setFieldValue("state", value)}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            options={STATES}
                                            getOptionLabel={(option) => `${option.displayName} - ${option.abbreviation}`}
                                            renderInput={(params) =>
                                                <TextField {...params}
                                                    name={`state`}
                                                    label="State"
                                                    error={Boolean(touched.state && errors.state)}
                                                    helperText={touched.state && errors.state}
                                                    margin="normal" />
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Primary Facility"
                                            name="primaryFacility"
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.primaryFacility}
                                            helperText={touched.primaryFacility && errors.primaryFacility}
                                            error={Boolean(touched.primaryFacility && errors.primaryFacility)}
                                            inputProps={{ maxLength: 250 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="isActive"
                                                    color="primary"
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className="ml-0"
                                                    value={values.isActive}
                                                />
                                            }
                                            label="Active"
                                            className={classes.switch}
                                        />
                                    </Grid>
                                    <Grid
                                        container
                                        className="justify-content-end pt-2 mt-3"
                                    >
                                        <Button type="button" onClick={() => handleClose()} variant="contained" className="mr-3">
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>)
                        }}
                    </Formik>
                </Loadable>
            </DialogContent>
            </Loadable>
        </Dialog>)
    );
}